import React, { FunctionComponent } from 'react'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { BodyTextSize, TextAlign } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'
import { GenericModalWrapper } from '../genericModalWrapper/GenericModalWrapper'

export interface TextModalProps {
  /** Controls modal visibility */
  isOpen: boolean
  /** Called by the close (X) icon and the background scrim */
  onClose: () => void
  /** Already-localized title (always rendered). A plain string, or a rich node (e.g. `FormattedMessage` with bold chunks). */
  title: React.ReactNode
  /** Already-localized secondary heading rendered directly under the title */
  subhead?: string
  /** Already-localized description string (rendered when provided) */
  description?: string | React.ReactNode
  /** Primary action. When omitted, the primary button is not rendered. Does NOT auto-close — the caller controls closing. */
  primaryCTA?: () => void
  /** Secondary action. When omitted, the secondary button is not rendered. Does NOT auto-close — the caller controls closing. */
  secondaryCTA?: () => void
  /** When provided, the illustration is shown above the title/subhead/description. */
  illustrationElement?: React.ReactElement
  /** Alignment of the title/subhead/description. Defaults to `'center'`. */
  textAlignment?: TextAlign
  /** Optional custom content rendered after the description (alignment is the caller's responsibility). */
  contentElement?: React.ReactElement
  /** Primary button label. Defaults to "Continue". */
  primaryButtonText?: React.ReactNode
  /** Secondary button label. Defaults to "Cancel". */
  secondaryButtonText?: React.ReactNode
  /** Override the primary button's testID. Defaults to `${testID}-primaryButton`. */
  primaryButtonTestID?: string
  /** Override the description's testID. Defaults to `${testID}-description`. */
  descriptionTestID?: string
  /** Override the secondary button's testID. Defaults to `${testID}-secondaryButton`. */
  secondaryButtonTestID?: string
  /** Override the close (X) icon's testID. Defaults to `${testID}-closeIcon`. */
  closeIconTestID?: string
  /** Bottom-sheet snap points (mobile-sized web). Defaults to `['90%']`. */
  snapPoints?: (string | number)[]
  /** Desktop pop-up width (passed to Modal). Defaults to Modal's `'544px'`. */
  width?: string
  /** Accessibility label for the modal. Falls back to `title` when it is a plain string. */
  accessibilityLabel?: string
  /** Native ID applied to the content container (maps to the DOM `id` on web). */
  nativeID?: string
  testID?: string
}

/**
 * Standardized generic text modal (web, Aurora + Select). A thin wrapper over `GenericModalWrapper`
 * that renders its `description` (and optional `contentElement`) into the frame's content slot.
 * Intended to replace bespoke one-off text-modal wrappers.
 */
export const TextModal: FunctionComponent<TextModalProps> = ({
  isOpen,
  onClose,
  title,
  subhead,
  description,
  primaryCTA,
  secondaryCTA,
  illustrationElement,
  textAlignment = 'left',
  descriptionTestID,
  contentElement,
  primaryButtonText,
  secondaryButtonText,
  primaryButtonTestID,
  secondaryButtonTestID,
  closeIconTestID,
  snapPoints,
  width,
  accessibilityLabel,
  nativeID,
  testID = 'TextModal',
}) => {
  const textAlign: TextAlign = textAlignment

  const content = (
    <>
      {Boolean(description) && (
        <BodyText
          size={BodyTextSize.DEFAULT}
          text={description}
          textAlign={textAlign}
          testID={descriptionTestID ?? tID(`${testID}-description`)}
        />
      )}
      {contentElement}
    </>
  )

  return (
    <GenericModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subhead={subhead}
      content={content}
      illustrationElement={illustrationElement}
      textAlignment={textAlignment}
      primaryCTA={primaryCTA}
      secondaryCTA={secondaryCTA}
      primaryButtonText={primaryButtonText}
      secondaryButtonText={secondaryButtonText}
      primaryButtonTestID={primaryButtonTestID}
      secondaryButtonTestID={secondaryButtonTestID}
      closeIconTestID={closeIconTestID}
      snapPoints={snapPoints}
      width={width}
      accessibilityLabel={accessibilityLabel}
      nativeID={nativeID}
      testID={testID}
    />
  )
}
